import React from "react";
import styled from "styled-components";
import { AlertOctagon, CheckCircle, X } from "react-feather";
import { Button, Spinner, Status } from "@cinesys/ui/components";
import { useUpdateComponentsJob } from "../context/UpdateComponentsJobContext";

const Wrapper = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 320px;
  pointer-events: none;
`;

const Card = styled.div`
  pointer-events: all;
  background-color: var(--dark100);
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  min-width: 260px;
`;

const CardHeader = styled.div`
  background-color: var(--dark300);
  border-bottom: 1px solid var(--dark060);
  min-height: 45px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.div<{ $color?: string }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${(p) => p.$color ?? "var(--gray200)"};
`;

const Label = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--gray400);
  display: flex;
  align-items: center;
`;

const CardBody = styled.div`
  padding: 10px 10px 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
  background-color: var(--dark200);
`;

const UpdateComponentsJobToast = () => {
  const { jobs, dismissJob } = useUpdateComponentsJob();

  if (!jobs.length) {
    return null;
  }

  return (
    <Wrapper>
      {jobs.map((job) => {
        const hasErrors =
          job.status === "complete" &&
          job.results &&
          job.results.errorCount > 0;
        const isSuccess = job.status === "complete" && !job.error && !hasErrors;
        const isError =
          job.status === "error" || (job.status === "complete" && !!job.error);

        return (
          <Card key={job.scriptExecutionId}>
            <CardHeader>
              {job.status === "running" && (
                <IconWrapper>
                  <Spinner small />
                </IconWrapper>
              )}
              {isSuccess && (
                <IconWrapper $color="var(--green500)">
                  <CheckCircle size={16} />
                </IconWrapper>
              )}
              {(isError || hasErrors) && (
                <IconWrapper $color={"var(--red400)"}>
                  <AlertOctagon size={16} />
                </IconWrapper>
              )}
              <Label>
                {isSuccess
                  ? job.label.replace(/^Updating/, "Updated")
                  : isError || hasErrors
                  ? job.label.replace(/^Updating/, "Update") + " failed"
                  : `${job.label}...`}
              </Label>
              {job.status !== "running" && (
                <Button
                  icon={X}
                  onClick={() => dismissJob(job.scriptExecutionId)}
                  borderless
                />
              )}
            </CardHeader>
            {(isError || hasErrors) && (
              <CardBody>
                {isError && <Status type="error">{job.error}</Status>}
                {hasErrors &&
                  Object.entries(job.results!.errors).map(([sd, msg]) => (
                    <Status key={sd} type="error">
                      {sd}: {msg}
                    </Status>
                  ))}
              </CardBody>
            )}
          </Card>
        );
      })}
    </Wrapper>
  );
};

export default UpdateComponentsJobToast;
